import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { InAppNotification } from '@terra/api-client';
import { Spinner } from '@terra/ui';

import { api } from '../lib/api';

/**
 * The seller's inbox: new orders to accept, cancellations, and anything else
 * addressed to them. Polls the unread count so a new order shows up without a
 * reload — a seller who has to refresh to find out they have work is how
 * orders miss their dispatch window.
 */
export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const unread = useQuery({
    queryKey: ['vendor-inbox-unread'],
    queryFn: () => api.notifications.myUnreadCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const list = useQuery({
    queryKey: ['vendor-inbox'],
    queryFn: () => api.notifications.myList({ pageSize: 15 }),
    enabled: open,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['vendor-inbox'] });
    void qc.invalidateQueries({ queryKey: ['vendor-inbox-unread'] });
  };
  const markRead = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: refresh,
  });
  const markAllRead = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: refresh,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (n: InAppNotification) => {
    if (!n.isRead) markRead.mutate(n.id);
    setOpen(false);
    if (n.refType === 'VendorSubOrder' && n.refId) navigate(`/orders/${n.refId}`);
    else if (n.refType === 'SupportTicket' && n.refId) navigate(`/customer-support/${n.refId}`);
    else if (n.refType === 'ProductQuery' || n.refType === 'ProductConversation') {
      navigate('/product-support');
    } else if (n.refType === 'PayoutRequest') navigate('/wallet');
  };

  const count = unread.data ?? 0;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="border-border text-ink-muted hover:border-gold-300 hover:text-gold-700 relative grid h-10 w-10 place-items-center rounded-xl border transition-all duration-200"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span className="ring-surface absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="border-border bg-surface absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border shadow-xl">
          <div className="border-border flex items-center justify-between border-b px-4 py-3">
            <p className="text-ink text-sm font-bold">Notifications</p>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="text-ink-muted hover:text-ink text-xs font-semibold"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[26rem] overflow-y-auto">
            {list.isLoading ? (
              <div className="grid place-items-center py-8">
                <Spinner size="sm" />
              </div>
            ) : (list.data?.data ?? []).length === 0 ? (
              <p className="text-ink-muted px-4 py-8 text-center text-sm">
                Nothing yet. New orders will show up here.
              </p>
            ) : (
              <ul>
                {(list.data?.data ?? []).map((n) => (
                  <li key={n.id} className="border-border border-b last:border-b-0">
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      className={`hover:bg-surface-subtle w-full px-4 py-3 text-left ${
                        n.isRead ? '' : 'bg-amber-50/60'
                      }`}
                    >
                      <p className="text-ink flex items-center gap-2 text-sm font-semibold">
                        {!n.isRead && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />
                        )}
                        {n.title}
                      </p>
                      <p className="text-ink-muted mt-0.5 text-xs leading-relaxed">{n.body}</p>
                      <p className="text-ink-subtle mt-1 text-[11px]">
                        {new Date(n.createdAt).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
